import React, {FC, useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {useAuth} from '../../auth'
import SearchableSelect from '../../../shared/components/SearchableSelect'

type AttachmentItem = {
  id: string
  fileName: string
  viewUrl: string
  downloadUrl: string
  type: string
  typeLabel?: string
  sizeBytes?: number
  mimeType?: string | null
}

type SCMAttachmentsModalProps = {
  visible: boolean
  title: string
  subtitle?: string | null
  loading: boolean
  items: AttachmentItem[]
  onClose: () => void
  onViewFile: (item: AttachmentItem) => void
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 2100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const SCMAttachmentsModal: FC<SCMAttachmentsModalProps> = ({
  visible,
  title,
  subtitle,
  loading,
  items,
  onClose,
  onViewFile,
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null)

  const {auth} = useAuth()
  const token = auth?.auth?.jwt

  useEffect(() => {
    if (visible) {
      setCurrentPage(1)
      setDownloadingFile(null)
    }
  }, [visible, items.length])

  if (!visible) return null

  const totalItems = items.length
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage))

  const indexOfLast = currentPage * rowsPerPage
  const indexOfFirst = indexOfLast - rowsPerPage
  const pageItems = items.slice(indexOfFirst, indexOfLast)

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const spinner = (
    <div className='text-center py-5'>
      <div className='spinner-border' role='status' aria-label='Loading'>
        <span className='visually-hidden'>Loading...</span>
      </div>
    </div>
  )

  const handleBackdropClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const formatBytes = (n?: number) => {
    const v = Number(n ?? 0)
    if (!Number.isFinite(v) || v <= 0) return '-'
    const units = ['B', 'KB', 'MB', 'GB']
    let size = v
    let i = 0
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024
      i++
    }
    const digits = i === 0 ? 0 : 1
    return `${size.toFixed(digits)} ${units[i]}`
  }

  const handleDownloadClick = async (item: AttachmentItem) => {
    try {
      if (!item.downloadUrl) return
      setDownloadingFile(item.fileName)

      const res = await fetch(item.downloadUrl, {
        headers: token ? {Authorization: `Bearer ${token}`} : undefined,
      })
      if (!res.ok) {
        throw new Error(`Download failed: ${res.status}`)
      }

      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = item.fileName || 'download'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e: any) {
      console.error(e)
      toast.error('Download failed', {position: 'top-center'})
    } finally {
      setDownloadingFile(null)
    }
  }

  return (
    <div style={WRAP_STYLE} onClick={handleBackdropClick}>
      <div
        className='bg-white rounded shadow-lg d-flex flex-column'
        style={{
          width: '100%',
          maxWidth: '800px',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div className='d-flex justify-content-between align-items-center border-bottom px-4 py-3'>
          <div>
            <h5 className='mx-6 mt-6'>{title}</h5>
            <div className='d-flex align-items-center gap-4 mx-6 '>
              {subtitle && <small className='text-muted'>{subtitle}</small>}
              <small className='text-muted'>
                | &nbsp; Total attachments: <strong>{totalItems}</strong>
              </small>
            </div>
          </div>

          <button type='button' className='btn-close m-6' onClick={onClose} />
        </div>

        {/* Body with inner scroll */}
        <div className='px-4 py-3 flex-grow-1 m-6' style={{overflowY: 'auto', minHeight: '150px'}}>
          {loading ? (
            spinner
          ) : totalItems === 0 ? (
            <div className='text-muted text-center py-4'>No attachments found.</div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-sm align-middle mb-0'>
                <thead>
                  <tr className='bg-light-primary'>
                    <th style={{width: '60px'}}>Sr/No</th>
                    <th>File Name</th>
                    <th className='text-end pe-6' style={{width: '220px'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((it, idx) => (
                    <tr key={it.id || it.fileName + idx}>
                      <td>{indexOfFirst + idx + 1}</td>
                      <td>
                        <div className='text-truncate' style={{maxWidth: '360px'}} title={it.fileName}>
                          {it.fileName}
                        </div>
                        <div className='text-muted small mt-1 d-flex align-items-center gap-2 flex-wrap'>
                          <span className='badge badge-light' style={{fontWeight: 600}}>
                            {it.typeLabel ?? it.type}
                          </span>
                          <span>
                            Size: <span className='fw-semibold'>{formatBytes(it.sizeBytes)}</span>
                          </span>
                          {it.mimeType ? (
                            <span>
                              Type: <span className='fw-semibold'>{it.mimeType}</span>
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className='text-end'>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-primary me-2'
                          onClick={() => onViewFile(it)}
                        >
                          View
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-info'
                          onClick={() => handleDownloadClick(it)}
                          disabled={downloadingFile === it.fileName}
                        >
                          {downloadingFile === it.fileName ? 'Downloading...' : 'Download'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='d-flex align-items-center justify-content-between px-6 py-4 border-top'>
          <div className='text-muted small'>
            Page {currentPage} of {totalPages}
          </div>
          <div className='btn-group'>
            <button
              type='button'
              className='btn btn-sm btn-light'
              disabled={currentPage <= 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Prev
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light'
              disabled={currentPage >= totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </button>
          </div>
          <SearchableSelect
            className='form-select form-select-sm w-auto'
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[5, 10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </SearchableSelect>
        </div>
      </div>
    </div>
  )
}

export default SCMAttachmentsModal
